/**
 * /pay/:unitId/:linkId – public (no sign-in) page where people pay a payment link.
 * Phone-first: most people arrive by scanning the QR code at the meeting.
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, CalendarX, Loader2, PauseCircle, SearchX } from 'lucide-react';
import type { PublicPaymentLinkResponse } from '@masonic-tools/shared-types';
import { PublicPaymentLinkCard } from '@/components/payment-links/PublicPaymentLinkCard';
import { callableErrorInfo, confirmPaymentLinkPayment, createPaymentLinkCheckout, getPublicPaymentLink } from '@/lib/payment-links-api';
import { formatDate } from '@/lib/unit-payment-links';
import type { PayerFormInput } from '@/lib/unit-payment-links';
import { showInfo } from '@/lib/swal';

export function payerFormStorageKey(linkId: string): string {
  return `payment_link_form_${linkId}`;
}

function readSavedForm(linkId: string): Partial<PayerFormInput> | undefined {
  try {
    const raw = sessionStorage.getItem(payerFormStorageKey(linkId));
    return raw ? (JSON.parse(raw) as Partial<PayerFormInput>) : undefined;
  } catch {
    return undefined;
  }
}

function saveForm(linkId: string, form: PayerFormInput) {
  try {
    // Never keep the Gift Aid confirmation tick: payers re-confirm each time.
    sessionStorage.setItem(payerFormStorageKey(linkId), JSON.stringify({ ...form, giftAidConfirmed: false }));
  } catch {
    /* private mode */
  }
}

export function PublicPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-muted/40">
      <main className="mx-auto flex w-full max-w-[480px] flex-1 flex-col px-4 py-6 sm:py-10">{children}</main>
      <footer className="pb-6 text-center text-xs text-muted-foreground">Payments powered by Masonic Tools</footer>
    </div>
  );
}

function StateCard({ icon: Icon, title, text }: { icon: typeof AlertCircle; title: string; text: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center shadow-sm">
      <Icon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

export default function PublicPaymentLinkPage() {
  const { unitId = '', linkId = '' } = useParams<{ unitId: string; linkId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PublicPaymentLinkResponse | null>(null);
  const [loadError, setLoadError] = useState<'not_found' | 'error' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<{ message: string; field?: string } | null>(null);
  const [savedForm] = useState(() => readSavedForm(linkId));

  useEffect(() => {
    let cancelled = false;
    getPublicPaymentLink({ unitId, linkId })
      .then((r) => !cancelled && setData(r))
      .catch((e) => {
        if (cancelled) return;
        setLoadError(callableErrorInfo(e, '').code === 'functions/not-found' ? 'not_found' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [unitId, linkId]);

  // Back from the provider without paying.
  useEffect(() => {
    const cancelledPid = searchParams.get('cancelled');
    if (!cancelledPid) return;
    confirmPaymentLinkPayment({ unitId, linkId, paymentId: cancelledPid, cancelled: true }).catch(() => undefined);
    showInfo('Payment cancelled. No money was taken.');
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.delete('cancelled');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, unitId, linkId]);

  useEffect(() => {
    if (data?.link.title) document.title = `${data.link.title} · ${data.unit.name}`;
  }, [data]);

  const handleSubmit = async (form: PayerFormInput) => {
    if (!data) return;
    setSubmitting(true);
    setServerError(null);
    saveForm(linkId, form);
    try {
      const result = await createPaymentLinkCheckout({
        unitId,
        linkId,
        amountPence: data.link.amountType === 'fixed' ? data.link.fixedAmountPence ?? 0 : form.amountPence ?? 0,
        ...(form.giveAnonymously ? {} : { payerName: form.payerName.trim(), payerEmail: form.payerEmail.trim() }),
        ...(form.message.trim() ? { message: form.message.trim() } : {}),
        ...(form.giftAid
          ? {
              giftAid: {
                confirmed: form.giftAidConfirmed,
                title: form.giftAidTitle.trim(),
                firstName: form.giftAidFirstName.trim(),
                lastName: form.giftAidLastName.trim(),
                houseNameOrNumber: form.giftAidHouse.trim(),
                postcode: form.giftAidPostcode.trim(),
              },
            }
          : {}),
      });
      window.location.assign(result.checkoutUrl);
    } catch (e) {
      const info = callableErrorInfo(e, 'Could not start the payment. Please try again.');
      setServerError(info);
      setSubmitting(false);
      // The link may have closed while the page was open.
      getPublicPaymentLink({ unitId, linkId }).then(setData).catch(() => undefined);
    }
  };

  if (loadError === 'not_found') {
    return (
      <PublicPageShell>
        <StateCard icon={SearchX} title="Payment link not found" text="Check the link or QR code and try again." />
      </PublicPageShell>
    );
  }
  if (loadError) {
    return (
      <PublicPageShell>
        <StateCard icon={AlertCircle} title="Something went wrong" text="We couldn't load this payment link. Please refresh the page to try again." />
      </PublicPageShell>
    );
  }
  if (!data) {
    return (
      <PublicPageShell>
        <div className="flex flex-1 items-center justify-center" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading</span>
        </div>
      </PublicPageShell>
    );
  }

  if (data.state !== 'active') {
    const closedOn = data.link.expiresAtMs ? formatDate(new Date(data.link.expiresAtMs)) : '';
    const content =
      data.state === 'expired'
        ? { icon: CalendarX, title: data.link.title, text: `This collection closed${closedOn ? ` on ${closedOn}` : ''}. Thank you for your support.` }
        : data.state === 'method_unavailable'
          ? { icon: AlertCircle, title: data.link.title, text: `Payments are temporarily unavailable. Please contact ${data.unit.name}.` }
          : { icon: PauseCircle, title: data.link.title, text: 'This link is no longer accepting payments.' };
    return (
      <PublicPageShell>
        <p className="mb-3 text-center text-sm text-muted-foreground">{data.unit.name}</p>
        <StateCard {...content} />
      </PublicPageShell>
    );
  }

  return (
    <PublicPageShell>
      <PublicPaymentLinkCard
        unitName={data.unit.name}
        logoUrl={data.unit.logoUrl}
        link={data.link}
        giftAidTemplate={data.giftAid.template}
        submitting={submitting}
        serverError={serverError}
        initialForm={savedForm}
        onSubmit={handleSubmit}
      />
    </PublicPageShell>
  );
}
